/** @jsxImportSource react */
import { useEffect, useMemo, useState } from 'react'
import {
  Alert, Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, MenuItem, Paper, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { addMonths, format } from 'date-fns'
import { forecastAPI } from '../api/crm'
import { useEntityTypes, usePublishedForm, useTenantKey } from '../hooks/useCrmQueries'
import { selectIsTenantAdmin } from '../store/slices/tenantSlice'
import { allFields } from '../components/records/fieldValues'
import { assignable, useMembers } from '../components/records/PersonInput'
import PageHeader from '../components/common/PageHeader'
import Loading from '../components/common/Loading'
import { useNotify } from '../components/common/NotificationProvider'
import { entityLabelPlural } from '../utils/helpers'
import { getErrorMessage } from '../utils/apiErrors'
import { formatCompact, formatNumber } from '../utils/numbers'

const n = (v) => (v === null || v === undefined ? null : Number(v))

/**
 * Won (solid), weighted open pipeline (outlined, same hue: fill tells them apart, not colour) and
 * the target (a thin tick), on one scale shared by every row.
 */
const Meter = ({ line, max, type }) => {
  const theme = useTheme()
  const c = theme.palette.chart.series1
  const pct = (v) => `${Math.max(0, Math.min(100, (n(v) / max) * 100))}%`
  return (
    <Tooltip title={`Won ${formatNumber(line.won, type)} · weighted pipeline ${formatNumber(line.weighted, type)}${line.target !== null ? ` · target ${formatNumber(line.target, type)}` : ''}`}>
      <Box sx={{ position: 'relative', height: 18, minWidth: 140 }}>
        <Box sx={{ position: 'absolute', top: 4, height: 10, left: 0, width: pct(line.won), bgcolor: c, borderRadius: '2px 0 0 2px' }} />
        {n(line.weighted) > 0 && (
          <Box sx={{ position: 'absolute', top: 4, height: 10, boxSizing: 'border-box', border: 1.5, borderColor: c, borderRadius: '0 4px 4px 0',
            left: `calc(${pct(line.won)} + ${n(line.won) > 0 ? 2 : 0}px)`, width: pct(line.weighted) }} />
        )}
        {line.target !== null && max > 0 && (
          <Box sx={{ position: 'absolute', top: 0, height: 18, width: 2, bgcolor: 'text.primary', left: `calc(${pct(line.target)} - 1px)` }} />
        )}
      </Box>
    </Tooltip>
  )
}

const Legend = () => {
  const theme = useTheme()
  const c = theme.palette.chart.series1
  const item = (mark, label) => <Stack direction="row" spacing={0.75} alignItems="center">{mark}<Typography variant="caption" color="text.secondary">{label}</Typography></Stack>
  return (
    <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
      {item(<Box sx={{ width: 14, height: 10, bgcolor: c, borderRadius: 0.5 }} />, 'Won')}
      {item(<Box sx={{ width: 14, height: 10, border: 1.5, borderColor: c, borderRadius: 0.5, boxSizing: 'border-box' }} />, 'Weighted pipeline')}
      {item(<Box sx={{ width: 2, height: 14, bgcolor: 'text.primary' }} />, 'Target')}
    </Stack>
  )
}

const Stat = ({ label, value, type, note }) => (
  <Paper variant="outlined" sx={{ p: 1.5, flex: 1, minWidth: 140 }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography sx={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2 }}>{value === null ? '—' : formatCompact(value, type)}</Typography>
    {note && <Typography variant="caption" color="text.secondary">{note}</Typography>}
  </Paper>
)

const ForecastPage = () => {
  const tenantId = useTenantKey()
  const isAdmin = useSelector(selectIsTenantAdmin)
  const settings = useQuery({ queryKey: [tenantId, 'forecast', 'settings'], queryFn: forecastAPI.settings })
  const configured = Object.keys(settings.data || {})
  const [entityType, setEntityType] = useState('')
  const [period, setPeriod] = useState('MONTH')
  const [date, setDate] = useState(new Date())
  const [setup, setSetup] = useState(false)
  const [targets, setTargets] = useState(false)
  useEffect(() => { if (!entityType && configured.length) setEntityType(configured[0]) }, [configured, entityType])

  const params = { entityType, period, date: format(date, 'yyyy-MM-dd') }
  const forecast = useQuery({ queryKey: [tenantId, 'forecast', params], queryFn: () => forecastAPI.get(params), enabled: !!entityType, placeholderData: (p) => p })
  const f = forecast.data
  const type = f?.amountType
  const max = useMemo(() => Math.max(1, ...(f?.people || []).map((l) => Math.max(n(l.won) + n(l.weighted), n(l.target) || 0))), [f])

  if (settings.isLoading) return <Loading />
  return (
    <Box>
      <PageHeader title="Forecast" subtitle="Won so far, plus open deals weighted by how likely each stage is to close."
        actions={isAdmin && (
          <Stack direction="row" spacing={1}>
            {f && <Button onClick={() => setTargets(true)}>Targets</Button>}
            <Button variant={configured.length ? 'outlined' : 'contained'} onClick={() => setSetup(true)}>{configured.length ? 'Settings' : 'Set up forecasting'}</Button>
          </Stack>
        )} />
      {configured.length === 0 ? (
        <Alert severity="info">{isAdmin ? 'Choose which record type holds your deals, and which fields are the amount, close date, stage and owner.' : 'Forecasting isn’t set up yet; ask an admin.'}</Alert>
      ) : (
        <>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
            {configured.length > 1 && (
              <TextField select size="small" value={entityType} onChange={(e) => setEntityType(e.target.value)} sx={{ minWidth: 160 }}>
                {configured.map((t) => <MenuItem key={t} value={t}>{entityLabelPlural(t)}</MenuItem>)}
              </TextField>
            )}
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <IconButton size="small" aria-label="Previous" onClick={() => setDate((d) => addMonths(d, period === 'MONTH' ? -1 : -3))}><ChevronLeftIcon /></IconButton>
              <IconButton size="small" aria-label="Next" onClick={() => setDate((d) => addMonths(d, period === 'MONTH' ? 1 : 3))}><ChevronRightIcon /></IconButton>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, ml: 1 }}>{f?.label}</Typography>
              <Button size="small" onClick={() => setDate(new Date())} sx={{ ml: 1 }}>This {period === 'MONTH' ? 'month' : 'quarter'}</Button>
            </Stack>
            <Box sx={{ flexGrow: 1 }} />
            <ToggleButtonGroup size="small" exclusive value={period} onChange={(_, v) => v && setPeriod(v)}>
              <ToggleButton value="MONTH">Month</ToggleButton>
              <ToggleButton value="QUARTER">Quarter</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          {forecast.error && <Alert severity="error" sx={{ mb: 2 }}>{getErrorMessage(forecast.error)}</Alert>}
          {f && (
            <Box sx={{ opacity: forecast.isFetching ? 0.6 : 1 }}>
              <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1.5 }}>
                <Stat label="Won" value={f.total.won} type={type} note={`${f.total.wonCount} deals`} />
                <Stat label="Weighted pipeline" value={f.total.weighted} type={type} note={`of ${formatCompact(f.total.pipeline, type)} open in ${f.total.openCount} deals`} />
                <Stat label="Forecast" value={f.total.forecast} type={type} note="won + weighted" />
                <Stat label="Target" value={f.total.target} type={type}
                  note={f.total.target === null ? 'not set' : `${f.total.attainment ?? 0}% won · gap ${formatCompact(f.total.gap, type)}`} />
              </Stack>
              <Paper variant="outlined" sx={{ mb: 2 }}>
                <Box sx={{ px: 2, pt: 1.5 }}><Typography variant="subtitle2" sx={{ mb: 0.5 }}>By person</Typography><Legend /></Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {['Person', '', 'Won', 'Open pipeline', 'Weighted', 'Forecast', 'Target', 'Gap'].map((h, i) => (
                          <TableCell key={i} align={i > 1 ? 'right' : 'left'} sx={{ fontWeight: 600, whiteSpace: 'nowrap', width: i === 1 ? '28%' : undefined }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {f.people.length === 0 && <TableRow><TableCell colSpan={8}><Typography variant="body2" color="text.secondary">No deals close in this period.</Typography></TableCell></TableRow>}
                      {f.people.map((l) => (
                        <TableRow key={l.person?.id || l.name} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{l.name}</TableCell>
                          <TableCell><Meter line={l} max={max} type={type} /></TableCell>
                          <TableCell align="right">{formatNumber(l.won, type)}</TableCell>
                          <TableCell align="right">{formatNumber(l.pipeline, type)} <Typography component="span" variant="caption" color="text.secondary">({l.openCount})</Typography></TableCell>
                          <TableCell align="right">{formatNumber(l.weighted, type)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>{formatNumber(l.forecast, type)}</TableCell>
                          <TableCell align="right">{l.target === null ? '—' : <>{formatNumber(l.target, type)} <Typography component="span" variant="caption" color="text.secondary">({l.attainment}%)</Typography></>}</TableCell>
                          <TableCell align="right">{l.gap === null ? '—' : n(l.gap) <= 0 ? 'Covered' : formatNumber(l.gap, type)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
              <Paper variant="outlined">
                <Box sx={{ px: 2, pt: 1.5 }}><Typography variant="subtitle2">By stage</Typography></Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {['Stage', 'Chance', 'Deals', 'Amount', 'Weighted'].map((h, i) => <TableCell key={h} align={i ? 'right' : 'left'} sx={{ fontWeight: 600 }}>{h}</TableCell>)}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {f.stages.map((s) => (
                        <TableRow key={s.value}>
                          <TableCell>{s.label}{s.outcome !== 'OPEN' && <Typography component="span" variant="caption" color="text.secondary"> · {s.outcome === 'WON' ? 'won' : 'lost'}</Typography>}</TableCell>
                          <TableCell align="right">{s.outcome === 'OPEN' ? `${s.probability}%` : '—'}</TableCell>
                          <TableCell align="right">{s.count}</TableCell>
                          <TableCell align="right">{formatNumber(s.amount, type)}</TableCell>
                          <TableCell align="right">{s.outcome === 'OPEN' ? formatNumber(s.weighted, type) : '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Box>
          )}
        </>
      )}
      {setup && <SettingsDialog current={settings.data || {}} initialType={entityType} onClose={() => setSetup(false)} onSaved={(t) => { setEntityType(t); setSetup(false) }} />}
      {targets && f && <TargetsDialog forecast={f} entityType={entityType} onClose={() => setTargets(false)} />}
    </Box>
  )
}

/** Which record type and fields the forecast uses, stage chances, won / lost stages (admins). */
const SettingsDialog = ({ current, initialType, onClose, onSaved }) => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const notify = useNotify()
  const { entityTypes, forms } = useEntityTypes()
  const [entityType, setEntityType] = useState(initialType || entityTypes[0] || '')
  const { data: form } = usePublishedForm(forms.find((x) => x.entityType === entityType)?.code)
  const fields = allFields(form)
  const s = current[entityType] || {}
  const [v, setV] = useState({})
  useEffect(() => { setV({ amountField: s.amountField || '', closeDateField: s.closeDateField || '', stageField: s.stageField || '', ownerField: s.ownerField || '',
    probabilities: s.probabilities || {}, won: s.won || [], lost: s.lost || [] }) }, [entityType]) // eslint-disable-line react-hooks/exhaustive-deps
  const of = (types) => fields.filter((x) => types.includes(x.fieldType))
  const stage = fields.find((x) => x.fieldKey === v.stageField)
  const options = stage?.options || []
  const save = useMutation({
    mutationFn: () => forecastAPI.saveSettings(entityType, { ...v, ownerField: v.ownerField || null }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: [tenantId, 'forecast'] }); notify('Forecast settings saved'); onSaved(entityType) },
  })
  const pick = (label, key, types, optional) => (
    <TextField select size="small" label={label} value={v[key] || ''} onChange={(e) => setV({ ...v, [key]: e.target.value })}>
      {optional && <MenuItem value=""><em>None</em></MenuItem>}
      {of(types).map((x) => <MenuItem key={x.fieldKey} value={x.fieldKey}>{x.label}</MenuItem>)}
    </TextField>
  )
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Forecast settings</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {save.error && <Alert severity="error">{getErrorMessage(save.error)}</Alert>}
          <TextField select size="small" label="Deals are" value={entityType} onChange={(e) => setEntityType(e.target.value)}>
            {entityTypes.map((t) => <MenuItem key={t} value={t}>{entityLabelPlural(t)}</MenuItem>)}
          </TextField>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ '& > *': { flex: 1 } }}>
            {pick('Amount', 'amountField', ['NUMBER', 'DECIMAL', 'CURRENCY'])}
            {pick('Close date', 'closeDateField', ['DATE', 'DATETIME'])}
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ '& > *': { flex: 1 } }}>
            {pick('Stage', 'stageField', ['SELECT', 'RADIO'])}
            {pick('Owner', 'ownerField', ['USER'], true)}
          </Stack>
          {options.length > 0 && (
            <>
              <Autocomplete multiple size="small" options={options.map((o) => o.value)} value={v.won} getOptionLabel={(x) => options.find((o) => o.value === x)?.label || x}
                onChange={(_, won) => setV({ ...v, won })} renderInput={(p) => <TextField {...p} label="Won stages" />} />
              <Autocomplete multiple size="small" options={options.map((o) => o.value)} value={v.lost} getOptionLabel={(x) => options.find((o) => o.value === x)?.label || x}
                onChange={(_, lost) => setV({ ...v, lost })} renderInput={(p) => <TextField {...p} label="Lost stages" />} />
              <Typography variant="subtitle2">Chance of winning, per open stage</Typography>
              <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1.5 }}>
                {options.filter((o) => !v.won.includes(o.value) && !v.lost.includes(o.value)).map((o) => (
                  <TextField key={o.value} size="small" type="number" label={o.label} sx={{ width: 130 }} value={v.probabilities[o.value] ?? ''}
                    inputProps={{ min: 0, max: 100 }} InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    onChange={(e) => setV({ ...v, probabilities: { ...v.probabilities, [o.value]: e.target.value === '' ? undefined : Number(e.target.value) } })} />
                ))}
              </Stack>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={save.isPending || !entityType} onClick={() => save.mutate()}>Save</Button>
      </DialogActions>
    </Dialog>
  )
}

/** Targets for this period: the team, and each person (admins). Empty = no target. */
const TargetsDialog = ({ forecast, entityType, onClose }) => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const notify = useNotify()
  const { data: members = [] } = useMembers()
  const people = assignable(members)
  const initial = Object.fromEntries(forecast.people.filter((l) => l.person && l.target !== null).map((l) => [l.person.id, String(Number(l.target))]))
  const [values, setValues] = useState(initial)
  const [team, setTeam] = useState('')
  const save = useMutation({
    mutationFn: () => forecastAPI.saveTargets({
      entityType, period: forecast.period, periodStart: forecast.start,
      targets: [...people.map((m) => ({ userId: m.userId, amount: values[m.userId] === '' || values[m.userId] === undefined ? null : Number(values[m.userId]) })),
        ...(team !== '' ? [{ userId: null, amount: Number(team) }] : [])],
    }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: [tenantId, 'forecast'] }); notify('Targets saved'); onClose() },
  })
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Targets · {forecast.label}</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ pt: 1 }}>
          {save.error && <Alert severity="error">{getErrorMessage(save.error)}</Alert>}
          {people.map((m) => (
            <TextField key={m.userId} size="small" type="number" label={m.name || m.email} value={values[m.userId] ?? ''} inputProps={{ min: 0 }}
              onChange={(e) => setValues({ ...values, [m.userId]: e.target.value })} />
          ))}
          <TextField size="small" type="number" label="Whole team (optional)" value={team} inputProps={{ min: 0 }} onChange={(e) => setTeam(e.target.value)}
            helperText="Without it, the team target is the sum of people’s" />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={save.isPending} onClick={() => save.mutate()}>Save</Button>
      </DialogActions>
    </Dialog>
  )
}

export default ForecastPage
